import { ApplicationInitStatus, Component, computed, effect, HostListener, inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, signal, WritableSignal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LayoutComponent } from './commonComponent/layout/layout.component';
import { filter, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { AuthLayoutComponent } from './modules/auth/auth-layout/auth-layout.component';

import { QuickSearchComponent } from './modules/quickSearch/quick-search/quick-search.component';
import { Title } from '@angular/platform-browser';
import { AppConfigService } from '../appConfigService/config';
import { IdleService } from './commonServices/idel/idel.service';
import { SecureStorageEncryptDecryptService } from './commonServices/securityEncryptDecrypt/security-encrypt-decrypt.service';
import { MemoryManagementService } from './commonServices/memory-management/memory-management.service';
import { KeyboardShortcutComponent } from './commonComponent/keyboard-shortcut/keyboard-shortcut.component';
import { DropdownTemplatesComponent } from './commonComponent/formComponent/dropdown-templates/dropdown-templates.component';
import { Store } from '@ngrx/store';
import { selectIsLoggedIn, selectUserName } from './store/user/user.selector';
import { TooltipGlobalComponent } from './commonComponent/tooltip-global/tooltip-global.component';
import { UsaTimezoneClockComponent } from './commonComponent/usa-timezone-clock/usa-timezone-clock.component';
import { ClockWidgetService } from './commonComponent/usa-timezone-clock/clock-widget.service';
import { DateTimeFormatService } from './commonServices/date-time-format/date-time-format.service';
import { UsersService } from '../constants/usersDetails';
import { ThemeService } from '../services/theme/theme.service';
import { PopupComponent } from './commonComponent/popup/popup.component';
import { TrailerCompactDetailComponent } from './modules/assets/trailer/trailer-compact-detail/trailer-compact-detail.component';
import { CustomerCompactDetailComponent } from './modules/customer/customer-compact-detail/customer-compact-detail.component';
import { TruckCompactDetailComponent } from './modules/assets/truck/truck-compact-detail/truck-compact-detail.component';
import { DriverCompactDetailComponent } from './modules/assets/driver/driver-compact-detail/driver-compact-detail.component';
import { LoadDetailsComponent } from './modules/dispatch/load/load-details/load-details.component';
import { OverlayLoaderComponent } from './commonComponent/overlay-loader/overlay-loader.component';
import { AlertsMarqueeComponent } from './commonComponent/alerts-marquee/alerts-marquee.component';
import { MyRemindersComponent } from './commonComponent/my-reminders/my-reminders.component';
import { RemindersMarqueeComponent } from './commonComponent/reminders-marquee/reminders-marquee.component';
import { LaneListComponent } from './modules/dispatch/lane-list/lane-list.component';
import { DispatchDetailsComponent } from './modules/dispatch/running/dispatch-details/dispatch-details.component';
import { Helpers } from './utils/helpers';

interface AssetStackEntry {
  key: string;
  assetType: string;
  id: string | number;
  heading: string;
  loading: WritableSignal<boolean>;
}

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    LayoutComponent,
    AuthLayoutComponent,
    QuickSearchComponent,
    KeyboardShortcutComponent,
    TooltipGlobalComponent,
    DropdownTemplatesComponent,
    UsaTimezoneClockComponent,
    PopupComponent,
    TrailerCompactDetailComponent,
    CustomerCompactDetailComponent,
    TruckCompactDetailComponent,
    DriverCompactDetailComponent,
    LoadDetailsComponent,
    OverlayLoaderComponent,
    AlertsMarqueeComponent,
    MyRemindersComponent,
    RemindersMarqueeComponent,
    LaneListComponent,
    DispatchDetailsComponent
],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Alphaone';
  isLoggedIn = signal<boolean>(false);
  isLoading = signal<boolean>(true);
  quickSearch = signal<any>({});
  assetStack = signal<AssetStackEntry[]>([]);
  laneListPopup = signal<boolean>(false);
  private destroy$ = new Subject<void>();

  private idleService = inject(IdleService);
  private helper = inject(Helpers);
  private initStatus = inject(ApplicationInitStatus);
  private zone = inject(NgZone);
  clockWidget = inject(ClockWidgetService);
  // Inject ThemeService early so it applies the saved theme before first render
  private _theme = inject(ThemeService);

  private readonly platformId = inject(PLATFORM_ID);
  private readonly currentUrl = signal(isPlatformBrowser(this.platformId) && location.hash.startsWith('#/') ? location.hash.slice(1) : inject(Router).url);
  isFloatingRoute = computed(() => this.currentUrl().startsWith('/floating'));

  constructor(
    private titleService: Title,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private store: Store,
    private appConfig: AppConfigService,
    private secureStorage: SecureStorageEncryptDecryptService,
    private memoryManagement: MemoryManagementService,
    private dateTimeFormatService: DateTimeFormatService,
    private usersService: UsersService
  ) {
    effect(() => {
      this.store
        .select(selectIsLoggedIn)
        .pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.isLoggedIn.set(value);
          if (!value) {
            // When isLoggedIn becomes false (logout), force redirect
            this.router.navigate(['/auth/login'], { replaceUrl: true });
          }
        });
    });
  }

  async ngOnInit(): Promise<void> {
    if (typeof window === 'undefined') return;
    /* window `message` fires for ANY postMessage — browser extensions, devtools,
       sandboxed iframes — so it must not run in the zone: each event was costing a
       full app change-detection pass. Foreign messages now return for free; only a
       real cross-window navigation payload re-enters Angular. */
    this.zone.runOutsideAngular(() => window.addEventListener('message', this.onMessage));
    this.dateTimeFormatService.initFromUserDetail(this.usersService.USER?.detail);

    this.store
      .select(selectIsLoggedIn)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.isLoggedIn.set(value);
      });

    if (this.isLoggedIn()) {
      this.initStatus.donePromise.then(() => {
        // this.idleService.startWatching();
      });
    }
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event) => {
        this.currentUrl.set(event.urlAfterRedirects);
        await this.authentication();
      });

    this.activeRoute.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => this.syncAssetPopupFromParams(params));

    this.titleService.setTitle(this.appConfig?.AppConfigData?.appName);
    await this.authentication();
  }

  private syncAssetPopupFromParams(params: any): void {
    const detailType = params?.['detailType'];
    const detailId = params?.['detailId'];
    const stack = this.assetStack();

    if (detailType && detailId != null && detailId !== '') {
      const key = `${detailType}:${detailId}`;
      const index = stack.findIndex((entry) => entry.key === key);
      if (index === stack.length - 1 && index >= 0) return;
      if (index >= 0) {
        this.assetStack.set(stack.slice(0, index + 1));
        return;
      }
      const entry: AssetStackEntry = {
        key,
        assetType: detailType,
        id: detailId,
        heading: this.resolveAssetHeading(detailType, params?.['detailHeading']),
        loading: signal(detailType !== 'dispatch'),
      };
      this.assetStack.set([...stack, entry]);
      return;
    }

    if (stack.length) this.assetStack.set([]);
  }

  private resolveAssetHeading(detailType: string, headingParam?: string): string {
    if (detailType === 'load') {
      const num = String(headingParam ?? '').replace(/^\s*load\s*#?\s*/i, '').replace(/^#/, '').trim();
      return num ? 'Load #' + num : 'Load Details';
    }
    if (headingParam) return headingParam;
    const titles: Record<string, string> = { dispatch: 'Dispatch Details', truck: 'Truck Details', trailer: 'Trailer Details', driver: 'Driver Details', customer: 'Customer Details' };
    return titles[detailType] || 'Details';
  }

  private openAssetDetail(detailType: string, detailId: any, heading?: string): void {
    this.router.navigate([], {
      queryParams: { detailType, detailId, detailHeading: heading || null },
      queryParamsHandling: 'merge',
    });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') window.removeEventListener('message', this.onMessage);
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up memory management
    this.memoryManagement.cleanup();
  }

  private async authentication() {
    if (typeof window === 'undefined') return;

    try {
      const currentUrl = this.router.url;

      // 1. Define Public Routes that don't need a login
      const isPublicRoute = currentUrl.includes('/auth/login') || currentUrl.includes('/auth/forgot-password') || currentUrl.includes('/auth/reset-password');

      const userName = await firstValueFrom(this.store.select(selectUserName));
      const token = await this.secureStorage.getItem('accessToken', userName || 'USERNAME', 'logisticsAppSalt');

      if (this.isLoggedIn() && token) {
        // If logged in, don't allow access to login/forgot password pages
        if (isPublicRoute) {
          this.router.navigate([''], { replaceUrl: true });
        }
      } else {
        // 2. ONLY redirect to login if they are NOT already on a public route
        if (!isPublicRoute) {
          this.router.navigate(['/auth/login'], { replaceUrl: true });
        }
      }
    } catch (error) {
      this.isLoggedIn.set(false);
    } finally {
      this.isLoading.set(false);
    }
  }

  handleCloseQuickSearch(): void {
    this.quickSearch.set({
      trigger: false,
    });
  }

  closeAssetPopup(key: string): void {
    const stack = this.assetStack();
    const index = stack.findIndex((entry) => entry.key === key);
    if (index < 0) return;
    const remaining = stack.slice(0, index);
    this.assetStack.set(remaining);
    const top = remaining[remaining.length - 1];
    this.router.navigate([], {
      queryParams: top ? { detailType: top.assetType, detailId: top.id, detailHeading: top.heading, detailTab: null } : { detailType: null, detailId: null, detailHeading: null, detailTab: null },
      queryParamsHandling: 'merge',
    });
  }

  openLoadFromLaneList(load: { id: any; cuid: any }): void {
    if (load?.id == null) return;
    this.openAssetDetail('load', load.id, load?.cuid ? 'Load #' + load.cuid : 'Load Details');
  }

  private onMessage = (event: MessageEvent) => {
    const data: any = event?.data;
    if (data?.type === 'OPEN_ASSET_DETAIL') {
      const asset = data?.payload;
      if (asset?.assetType && asset?.id != null) {
        this.zone.run(() => this.openAssetDetail(asset.assetType, asset.id, asset.heading));
      }
      return;
    }
    const payload: any = data?.payload;
    if (!payload?.url) return;
    this.zone.run(() =>
      this.router.navigate([payload.url], {
        queryParams: payload?.queryParams,
        queryParamsHandling: '',
      })
    );
  };

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (event.ctrlKey && event.key === 'q') {
      event.preventDefault();
      this.quickSearch.set({
        trigger: true,
      });
    }
    if (event.ctrlKey && event.shiftKey && (event.key === 'L' || event.key === 'l')) {
      event.preventDefault();
      if (this.helper.handleCheckPermissions('DIS162')) {
        this.laneListPopup.set(true);
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.quickSearch.set({
        trigger: false,
      });
    }
  }
}
